import { uid } from './constants.js';

export const WALL_LENGTH_LOCK_TOLERANCE = 1e-6;
const id = v => typeof v === 'string' && v.trim() === v && v.length > 0 && [...v].length <= 80;
const text = (v, required = false) => typeof v === 'string' && v.trim() === v && (!required || v.length > 0) && [...v].length <= 500;
const date = v => {
  if (typeof v !== 'string' || v.length > 40 || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(v) || !Number.isFinite(Date.parse(v))) return false;
  const [y,m,d] = v.slice(0,10).split('-').map(Number);
  return m >= 1 && m <= 12 && d >= 1 && d <= new Date(Date.UTC(y === 0 ? 2000 : y,m,0)).getUTCDate();
};
const instant = v => BigInt(Math.floor(Date.parse(v) / 1000)) * 1000000000n + BigInt((v.match(/\.(\d+)/)?.[1] || '').slice(0,9).padEnd(9,'0'));
const fail = message => { throw new Error(message); };
const levels = doc => [{ id: doc.activeFloorId || 'ground', plan: doc }, ...(doc.floors || []).map(f => ({id:f.id,plan:f.plan}))];
const length = w => {
  if (!w || !['x1','y1','x2','y2'].every(k => Number.isFinite(w[k]) && Math.abs(w[k]) <= 100000)) return NaN;
  return Math.hypot(w.x2-w.x1,w.y2-w.y1);
};
const originalKeys = ['id','wallId','metres','source','createdAt','note','copiedFrom'];
const equal = (a,b) => JSON.stringify(a) === JSON.stringify(b);
function identity(lock) { return originalKeys.map(k => k === 'copiedFrom' && lock[k] ? [lock[k].lockId,lock[k].wallId,lock[k].floorID] : lock[k]); }
function entries(doc) { return levels(doc).flatMap(f => (f.plan.wallLengthLocks || []).map(lock => ({...f,lock}))); }
function validateRecord(lock) {
  if (!lock || typeof lock !== 'object' || Array.isArray(lock) || !id(lock.id) || !id(lock.wallId)
    || !Number.isFinite(lock.metres) || lock.metres < .1 || lock.metres > 300000
    || !['active','released'].includes(lock.state) || lock.source !== 'operator-reviewed-plan' || !date(lock.createdAt)
    || lock.note !== undefined && !text(lock.note)) fail('A wall length lock has invalid identity, length, source or review details.');
  if (Object.keys(lock).some(k => ![...originalKeys,'state','releasedAt','releaseReason'].includes(k))) fail('A wall length lock has unsupported fields.');
  if (lock.copiedFrom !== undefined && (!lock.copiedFrom || Array.isArray(lock.copiedFrom)
    || Object.keys(lock.copiedFrom).sort().join() !== 'floorID,lockId,wallId'
    || !id(lock.copiedFrom.lockId) || !id(lock.copiedFrom.wallId) || !id(lock.copiedFrom.floorID))) fail('A copied wall length lock has invalid provenance.');
  if (lock.state === 'released') {
    if (!date(lock.releasedAt) || instant(lock.releasedAt) < instant(lock.createdAt) || !text(lock.releaseReason,true)) fail('Record when and why this wall length was unlocked.');
  } else if (lock.releasedAt !== undefined || lock.releaseReason !== undefined) fail('An active wall length lock cannot contain release details.');
}

/** Validate actual geometry, not a survey-accuracy claim. No input is changed. */
export function validateWallLengthLocks(doc) {
  const seen = new Set();
  for (const {plan} of levels(doc)) {
    if (!plan || (plan.wallLengthLocks !== undefined && !Array.isArray(plan.wallLengthLocks)) || (plan.wallLengthLocks || []).length > 4000) fail('Each floor supports up to 4000 wall length lock records.');
    const active = new Set(), wallMap = new Map();
    for (const wall of plan.walls || []) wallMap.set(wall.id,[...(wallMap.get(wall.id) || []),wall]);
    for (const lock of plan.wallLengthLocks || []) {
      validateRecord(lock);
      if (seen.has(lock.id)) fail('Wall length lock IDs must be unique across the Site.');
      seen.add(lock.id);
      if (lock.state !== 'active') continue;
      if (active.has(lock.wallId)) fail('A wall can have only one active length lock.');
      active.add(lock.wallId);
      const hosts = wallMap.get(lock.wallId) || [];
      if (hosts.length !== 1 || !Number.isFinite(length(hosts[0])) || Math.abs(length(hosts[0])-lock.metres) > WALL_LENGTH_LOCK_TOLERANCE) fail(`Locked wall ${lock.wallId} must remain ${lock.metres} m long. Unlock it before changing its length or deleting it.`);
    }
  }
  return doc;
}

/** Detect older clients or snapshot replacements dropping constraints/history. */
export function assertWallLengthLockTransition(before,after) {
  validateWallLengthLocks(before); validateWallLengthLocks(after);
  const next = new Map(entries(after).map(e => [e.lock.id,e.lock]));
  for (const {lock} of entries(before)) {
    const found = next.get(lock.id);
    if (!found) fail('This edit would discard a wall length lock or its release history. Restore the record; explicitly unlock the wall before changing it.');
    if (!equal(identity(lock),identity(found))) fail('A recorded wall length lock cannot change its wall, required length or original review details.');
    if (lock.state === 'released' && (found.state !== 'released' || found.releasedAt !== lock.releasedAt || found.releaseReason !== lock.releaseReason)) fail('Released wall length history cannot be changed or reactivated. Create a new lock after review.');
  }
  return after;
}

export function wallLengthLockConflict(plan,wall,patch) {
  const locks = (plan.wallLengthLocks || []).filter(l => l.state === 'active' && l.wallId === wall.id);
  if (!locks.length) return '';
  const nextLength = length({...wall,...patch});
  return locks.some(l => !Number.isFinite(nextLength) || Math.abs(nextLength-l.metres) > WALL_LENGTH_LOCK_TOLERANCE)
    ? `This wall has a locked length of ${locks[0].metres} m. Unlock it before changing its length.` : '';
}

export function createWallLengthLock(plan,wallId,{note}={}) {
  validateWallLengthLocks(plan);
  const walls = (plan.walls || []).filter(w => w.id === wallId);
  if (walls.length !== 1) fail('Choose one existing wall to lock.');
  const lock = {id:uid('length-lock-'),wallId,metres:length(walls[0]),state:'active',createdAt:new Date().toISOString(),source:'operator-reviewed-plan',...(note !== undefined ? {note:typeof note === 'string' ? note.trim() : note} : {})};
  const doc = structuredClone(plan); doc.wallLengthLocks = [...(doc.wallLengthLocks || []),lock];
  validateWallLengthLocks(doc); return {doc,lock};
}

export function releaseWallLengthLock(plan,lockId,reason) {
  validateWallLengthLocks(plan);
  const doc = structuredClone(plan), lock = (doc.wallLengthLocks || []).find(l => l.id === lockId);
  if (!lock || lock.state !== 'active') fail('Choose an active wall length lock on this floor.');
  lock.state='released'; lock.releasedAt=new Date().toISOString(); lock.releaseReason=typeof reason === 'string' ? reason.trim() : reason;
  assertWallLengthLockTransition(plan,doc); return {doc,lock};
}

/** Copy active dimensional intent to new wall IDs; never label it a new measurement. */
export function copyWallLengthLocks(source,target,wallIDMap) {
  validateWallLengthLocks(source); validateWallLengthLocks(target);
  const doc = structuredClone(target);
  for (const lock of source.wallLengthLocks || []) {
    if (lock.state !== 'active') continue;
    const wallId = wallIDMap instanceof Map ? wallIDMap.get(lock.wallId) : wallIDMap?.[lock.wallId];
    if (!wallId) continue;
    if (wallId === lock.wallId) fail('Copied walls need new identities.');
    const copied = {...structuredClone(lock),id:uid('length-lock-'),wallId,createdAt:new Date().toISOString(),copiedFrom:{lockId:lock.id,wallId:lock.wallId,floorID:source.activeFloorId || 'ground'}};
    doc.wallLengthLocks=[...(doc.wallLengthLocks || []),copied];
  }
  validateWallLengthLocks(doc); return doc;
}

/** Restore geometry while keeping current lock/release history authoritative. */
export function preserveWallLengthLocksForRestore(current,target) {
  validateWallLengthLocks(current);
  const doc=structuredClone(target), targetLevels=levels(doc), records=entries(current);
  const currentHosts=new Map();
  for (const floor of levels(current)) for (const wall of floor.plan.walls || []) currentHosts.set(wall.id,(currentHosts.get(wall.id)||0)+1);
  const currentIDs=new Set(records.map(e=>e.lock.id)), floorMap=new Map(targetLevels.map(f=>[f.id,f])), hostsByID=new Map();
  for (const floor of targetLevels) for (const wall of floor.plan.walls || []) hostsByID.set(wall.id,[...(hostsByID.get(wall.id) || []),{floor,wall}]);
  for (const {plan} of targetLevels) plan.wallLengthLocks=(plan.wallLengthLocks || []).filter(l=>!currentIDs.has(l.id));
  for (const {id:floorID,lock} of records) {
    let destination=floorMap.get(floorID);
    if (lock.state === 'active') {
      const allHosts=hostsByID.get(lock.wallId) || [], localHosts=allHosts.filter(h=>h.floor.id===floorID);
      const hosts=localHosts.length ? localHosts : currentHosts.get(lock.wallId)===1 ? allHosts : [];
      if (hosts.length !== 1 || !Number.isFinite(length(hosts[0].wall))
        || Math.abs(length(hosts[0].wall)-lock.metres)>WALL_LENGTH_LOCK_TOLERANCE) fail('This restore would change or remove a locked wall. Unlock it before restoring this version.');
      destination=hosts[0].floor;
    }
    destination ||= targetLevels[0];
    destination.plan.wallLengthLocks.push(structuredClone(lock));
  }
  assertWallLengthLockTransition(current,doc); return doc;
}
